import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Plus,
  ArrowLeft,
  Radio,
  BookOpen,
  Pencil,
  Check,
  X,
} from 'lucide-react'
import type { QuestionBankItem } from '@/components/lessonBuilder/QuestionBankPicker'
import { questionBankItemToActivityAttrs } from '@/utils/questionBankUtils'
import { ActivitySidebar } from '@/components/lessonBuilder/ActivitySidebar'
import { ActivityPreview } from '@/components/lessonBuilder/ActivityPreview'
import { ActivityEditor } from '@/components/lessonBuilder/ActivityEditor'
import { apiClient } from '@/services/api'
import type { Lesson, LessonActivity } from '@/types'
import styles from './LessonBuilder.module.css'

interface LessonBuilderProps {
  readOnly?: boolean
}

export function LessonBuilder({ readOnly = false }: LessonBuilderProps) {
  const { lessonId } = useParams<{ lessonId: string }>()
  const navigate = useNavigate()

  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [activities, setActivities] = useState<LessonActivity[]>([])
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)

  const selectedActivity = activities.find(a => a.id === selectedActivityId)

  useEffect(() => {
    if (lessonId) {
      loadLessonAndActivities()
    }
  }, [lessonId])

  // Matrix rain effect
  useEffect(() => {
    const matrixEffect = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      if (!ctx) return

      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      canvas.style.position = 'fixed'
      canvas.style.top = '0'
      canvas.style.left = '0'
      canvas.style.zIndex = '0'
      canvas.style.pointerEvents = 'none'
      canvas.style.opacity = '0.1'

      document.body.appendChild(canvas)

      const matrix = '01000111011000010110110101100101010000100111010101101001011011000110010001100101011100100'
      const matrixArray = matrix.split('')

      const fontSize = 14
      const columns = canvas.width / fontSize
      const drops = Array(Math.floor(columns)).fill(1)

      function draw() {
        ctx.fillStyle = 'rgba(0, 8, 17, 0.1)'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        ctx.fillStyle = '#00ff88'
        ctx.font = `${fontSize}px Roboto Mono, monospace`

        for (let i = 0; i < drops.length; i++) {
          const text = matrixArray[Math.floor(Math.random() * matrixArray.length)]
          ctx.fillText(text, i * fontSize, drops[i] * fontSize)

          if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
            drops[i] = 0
          }
          drops[i]++
        }
      }

      const interval = setInterval(draw, 100)

      return () => {
        clearInterval(interval)
        document.body.removeChild(canvas)
      }
    }

    const cleanup = matrixEffect()
    return cleanup
  }, [])

  const loadLessonAndActivities = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load lesson details
      const lessonResponse = await apiClient.getLesson(lessonId!)
      const lessonData = lessonResponse.data?.lesson || lessonResponse.data
      setLesson(lessonData)

      // Load lesson activities
      const activitiesResponse = await apiClient.getLessonActivities(lessonId!)
      setActivities(activitiesResponse.data || [])

      // Select first activity if none selected
      if (activitiesResponse.data?.length > 0 && !selectedActivityId) {
        setSelectedActivityId(activitiesResponse.data[0].id)
      }
    } catch (err) {
      setError('Failed to load lesson data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddActivity = async (activityType: string) => {
    try {
      setSaving(true)

      const isSlideType = activityType === 'pdf_slideshow' || activityType === 'google_slides' || activityType === 'youtube_video'

      const newActivity = {
        activity_type: activityType,
        title: `${activityType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
        instruction: '',
        activity_data: getDefaultActivityData(activityType),
        sequence_number: activities.length > 0 ? Math.max(...activities.map(a => a.sequence_number)) + 1 : 1,
        time_limit_seconds: isSlideType ? null : 300 // slides have no time limit
      }

      const response = await apiClient.createActivity(lessonId!, { activity: newActivity })
      const createdActivity = response.data

      setActivities(prev => [...prev, createdActivity])
      setSelectedActivityId(createdActivity.id)
    } catch (err) {
      setError('Failed to create activity')
    } finally {
      setSaving(false)
    }
  }

  const handleBulkAddQuestions = async (questions: QuestionBankItem[]) => {
    try {
      setSaving(true)
      let maxSeq = activities.length > 0 ? Math.max(...activities.map(a => a.sequence_number)) : 0
      const created: LessonActivity[] = []

      for (const q of questions) {
        maxSeq++
        const attrs = questionBankItemToActivityAttrs(q)
        const newActivity = {
          ...attrs,
          sequence_number: maxSeq,
        }
        const response = await apiClient.createActivity(lessonId!, { activity: newActivity })
        created.push(response.data)
      }

      setActivities(prev => [...prev, ...created])
      if (created.length > 0) {
        setSelectedActivityId(created[0].id)
      }
    } catch (err) {
      setError('Failed to add random activities')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateActivity = async (activityId: string, updates: Partial<LessonActivity>) => {
    try {
      setSaving(true)

      const response = await apiClient.updateActivity(lessonId!, activityId, { activity: updates })
      const updatedActivity = response.data

      setActivities(prev =>
        prev.map(activity =>
          activity.id === activityId ? updatedActivity : activity
        )
      )
    } catch (err) {
      setError('Failed to update activity')
    } finally {
      setSaving(false)
    }
  }

  const handleReplaceWithQuestion = async (attrs: { activity_type: string; title: string; instruction: string; activity_data: any; time_limit_seconds: number | null }) => {
    if (!selectedActivityId || !lessonId) return
    try {
      setSaving(true)
      const response = await apiClient.updateActivity(lessonId, selectedActivityId, {
        activity: {
          title: attrs.title,
          instruction: attrs.instruction,
          activity_data: attrs.activity_data,
          time_limit_seconds: attrs.time_limit_seconds,
        }
      })
      const updatedActivity = response.data
      setActivities(prev =>
        prev.map(a => a.id === selectedActivityId ? updatedActivity : a)
      )
    } catch (err) {
      setError('Failed to update activity')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm('Are you sure you want to delete this activity?')) {
      return
    }

    try {
      setSaving(true)

      await apiClient.deleteActivity(lessonId!, activityId)

      setActivities(prev => prev.filter(activity => activity.id !== activityId))

      // Select another activity if the deleted one was selected
      if (selectedActivityId === activityId) {
        const remainingActivities = activities.filter(a => a.id !== activityId)
        setSelectedActivityId(remainingActivities.length > 0 ? remainingActivities[0].id : null)
      }
    } catch (err) {
      setError('Failed to delete activity')
    } finally {
      setSaving(false)
    }
  }

  const handleReorderActivities = async (reorderedActivities: LessonActivity[]) => {
    try {
      setSaving(true)

      // Update sequence numbers in state immediately for better UX
      const updates = reorderedActivities.map((activity, index) => ({
        ...activity,
        sequence_number: index + 1
      }))
      setActivities(updates)

      // Save to backend via bulk reorder endpoint
      await apiClient.reorderActivities(lessonId!, updates.map(a => a.id))
    } catch (err) {
      setError('Failed to save activity order')
      loadLessonAndActivities()
    } finally {
      setSaving(false)
    }
  }

  const handleStartEditName = () => {
    setNameValue(lesson?.name ?? '')
    setEditingName(true)
    setTimeout(() => nameInputRef.current?.focus(), 0)
  }

  const handleSaveName = async () => {
    const trimmed = nameValue.trim()
    if (!trimmed || !lessonId || trimmed === lesson?.name) {
      setEditingName(false)
      return
    }
    try {
      setSaving(true)
      await apiClient.updateLesson(lessonId, { lesson: { name: trimmed } })
      setLesson(prev => prev ? { ...prev, name: trimmed } : prev)
      setEditingName(false)
    } catch (err) {
      setError('Failed to update lesson name')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEditName = () => {
    setEditingName(false)
    setNameValue(lesson?.name ?? '')
  }

  const handleToggleMode = async () => {
    if (!lesson || !lessonId) return
    if (lesson.status !== 'draft') return

    const newMode = lesson.mode === 'live' ? 'self_paced' : 'live'
    try {
      setSaving(true)
      await apiClient.updateLesson(lessonId, { lesson: { mode: newMode } })
      setLesson(prev => prev ? { ...prev, mode: newMode } : prev)
    } catch (err) {
      setError('Failed to update lesson mode')
    } finally {
      setSaving(false)
    }
  }

  const getDefaultActivityData = (activityType: string): any => {
    switch (activityType) {
      case 'mcq':
        return {
          question: '',
          options: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
          correct_answer: 0
        }
      case 'pdf_slideshow':
        return {
          total_slides: 0,
          current_slide: 1,
          processing_status: 'pending'
        }
      case 'google_slides':
        return {
          google_slides_url: '',
          presentation_id: '',
          embed_url: ''
        }
      case 'youtube_video':
        return {
          youtube_url: '',
          video_id: '',
          embed_url: ''
        }
      case 'coding':
        return {
          question: 'Write a program that prints "Hello, World!"',
          test_cases: [{ id: 'test_1', description: 'Basic test', expected_output: 'Hello, World!' }],
          allowedCategories: ['Logic', 'Loops', 'Math', 'Text', 'Variables', 'Functions'],
          hints: []
        }
      case 'potato_pirates':
        return {
          question: 'Program your ships to destroy the enemy fleet!',
          objective: '',
          player_ships: [10, 10],
          opponent_ships: [10, 10],
          allowedCards: ['roast', 'mash', 'fry', 'for', 'while', 'ifelse'],
          hints: [],
          model_answer: [[], []],
        }
      case 'command_shell':
        return {
          question: '',
          target: '',
          hints: [],
          allowed_commands: [],
          correct_answer: '',
          case_sensitive: false
        }
      case 'phishing_quiz':
        return { question_id: null }
      case 'packet_analysis':
        return { question_id: null }
      case 'cyber_hygiene_quiz':
        return { question_id: null }
      case 'coding_quiz':
        return { question_id: null }
      case 'python_repl':
        return { question: '', starter_code: '# Write your code here\n', expected_output: '', test_cases: [], hints: [] }
      default:
        return {
          question: '',
          description: ''
        }
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <span className={styles.loadingText}>LOADING LESSON BUILDER</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorCard}>
          <p className={styles.errorText}>{error}</p>
          <button
            onClick={loadLessonAndActivities}
            className={styles.retryButton}
          >
            TRY AGAIN
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Background Effects */}
      <div className={styles.matrixRain}></div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div className={styles.titleSection}>
              {!readOnly && editingName ? (
                <div className={styles.nameEditRow}>
                  <input
                    ref={nameInputRef}
                    value={nameValue}
                    onChange={e => setNameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSaveName()
                      if (e.key === 'Escape') handleCancelEditName()
                    }}
                    className={styles.nameInput}
                    maxLength={100}
                  />
                  <button onClick={handleSaveName} className={styles.nameEditBtn} title="Save">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={handleCancelEditName} className={styles.nameEditBtn} title="Cancel">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <h1 onClick={readOnly ? undefined : handleStartEditName} className={readOnly ? undefined : styles.editableName} title={readOnly ? undefined : "Click to rename"}>
                  {lesson?.name?.toUpperCase() || 'LESSON PREVIEW'}
                  {!readOnly && <Pencil className="w-4 h-4" />}
                </h1>
              )}
              <p>
                {activities.length} {activities.length === 1 ? 'ITEM' : 'ITEMS'} LOADED
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
          </div>
        </div>
      </motion.div>

      {/* Three-panel layout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={styles.mainLayout}
      >
        {/* Left Sidebar - Activities List */}
        <div className={styles.sidebar}>
          <ActivitySidebar
            activities={activities}
            selectedActivityId={selectedActivityId}
            onSelectActivity={setSelectedActivityId}
            onAddActivity={handleAddActivity}
            onDeleteActivity={handleDeleteActivity}
            onReorderActivities={handleReorderActivities}
            loading={saving}
            readOnly={readOnly}
          />
        </div>

        {/* Middle Panel - Preview */}
        <div className={styles.previewPanel}>
          <ActivityPreview
            activity={selectedActivity}
            loading={saving}
            lessonId={lessonId}
          />
        </div>

        {/* Right Panel - Editor */}
        <div className={styles.editorPanel}>
          <ActivityEditor
            activity={selectedActivity}
            onUpdateActivity={handleUpdateActivity}
            onActivityRefresh={(id, updated) => {
              setActivities(prev => prev.map(a => a.id === id ? updated : a))
            }}
            onBulkAddQuestions={handleBulkAddQuestions}
            onReplaceWithQuestion={handleReplaceWithQuestion}
            loading={saving}
            lessonId={lessonId}
            readOnly={readOnly}
          />
        </div>
      </motion.div>
    </div>
  )
}
